import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ComputerDesktopIcon,
  CameraIcon,
  ArrowRightIcon,
  CheckIcon,
  ServerIcon,
  CpuChipIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';

const Reveal = ({ children, className = '', delayMs = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: visible ? `${delayMs}ms` : '0ms' }}
    >
      {children}
    </div>
  );
};

export const ProductsPage = () => {
  const products = [
    {
      name: 'DICOMFiles',
      role: 'Automatización del flujo DICOM',
      description:
        'Reduce el trabajo manual en la gestión de estudios y documentos: procesamiento continuo, integración con PACS y menos errores operativos.',
      icon: ComputerDesktopIcon,
      features: [
        'Procesamiento automático 24/7',
        'Integración con sistemas PACS',
        'Gestión de worklist DICOM',
        'Procesamiento de PDFs y códigos QR',
      ],
      cta: 'Consultar DICOMFiles',
      linkTo: '/contact',
      image: '/images/screenshots/dicomfiles/product.png',
      imageAlt: 'Captura de pantalla de DICOMFiles',
    },
    {
      name: 'DICOMCapture',
      role: 'Captura desde equipos médicos',
      description:
        'Conecta la adquisición de imágenes con el resto del flujo clínico: captura, validación y entrega ordenada hacia tus sistemas.',
      icon: CameraIcon,
      features: [
        'Captura desde equipos médicos',
        'Validación de calidad',
        'Procesamiento en tiempo real',
        'Control del flujo de trabajo',
      ],
      cta: 'Consultar DICOMCapture',
      linkTo: '/contact',
      image: '/images/screenshots/dicomcapture/product.png',
      imageAlt: 'Captura de pantalla de DICOMCapture',
    },
  ];

  const integrationPoints = [
    {
      icon: CpuChipIcon,
      title: 'Equipos de diagnóstico',
      description: 'Conexión con modalidades y dispositivos que hablan DICOM.',
    },
    {
      icon: ServerIcon,
      title: 'Sistemas PACS',
      description: 'Integración transparente para almacenamiento y distribución de estudios.',
    },
    {
      icon: ArrowsRightLeftIcon,
      title: 'Flujos clínicos',
      description: 'Menos pasos manuales entre captura, procesamiento y disponibilidad.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="animate-fade-up text-sm md:text-base font-semibold tracking-[0.2em] uppercase text-blue-200 mb-4">
            Productos
          </p>
          <h1
            className="animate-fade-up text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
            style={{ animationDelay: '100ms' }}
          >
            Soluciones de interoperabilidad
            <span className="block text-blue-300">para tu institución</span>
          </h1>
          <p
            className="animate-fade-up text-xl text-slate-200 max-w-3xl mx-auto"
            style={{ animationDelay: '220ms' }}
          >
            Diseñamos e implementamos herramientas que integran imágenes médicas,
            sistemas clínicos y procesos digitales — con DICOMFiles y DICOMCapture
            como piezas centrales del flujo.
          </p>
        </div>
      </section>

      {/* Productos */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {products.map((product, index) => {
            const Icon = product.icon;
            const reverse = index % 2 === 1;

            return (
              <Reveal key={product.name}>
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    reverse ? 'lg:[&>*:first-child]:order-2' : ''
                  }`}
                >
                  <div>
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-blue-50 text-blue-600 mb-5 transition-transform duration-300 hover:scale-110">
                      <Icon className="h-7 w-7" />
                    </div>
                    <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-2">
                      {product.role}
                    </p>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {product.name}
                    </h2>
                    <p className="text-lg text-gray-600 mb-8">{product.description}</p>
                    <ul className="space-y-3 mb-8">
                      {product.features.map((feature, featureIndex) => (
                        <li
                          key={feature}
                          className="flex items-start transition-transform duration-300 hover:translate-x-1"
                          style={{ transitionDelay: `${featureIndex * 40}ms` }}
                        >
                          <CheckIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={product.linkTo}
                      className="group inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                    >
                      {product.cta}
                      <ArrowRightIcon className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>

                  <div className="relative group">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.imageAlt}
                        className="w-full rounded-lg shadow-lg border border-gray-100 object-cover object-top max-h-[420px] transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="flex min-h-[320px] items-center justify-center rounded-lg bg-gradient-to-br from-slate-100 to-blue-50 border border-gray-100">
                        <div className="text-center px-8">
                          <CameraIcon className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                          <p className="text-lg font-semibold text-gray-900">DICOMCapture</p>
                          <p className="text-gray-600 mt-2">
                            Captura e integración desde el equipo hacia tu flujo clínico.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Integración */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cómo se integran
            </h2>
            <p className="text-xl text-gray-600">
              Encajan en la infraestructura que ya tenés: equipos, PACS y el día a
              día del centro, sin forzar un cambio de plataforma completo.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {integrationPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <Reveal key={point.title} delayMs={index * 120}>
                  <div className="transition-transform duration-300 hover:-translate-y-1">
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-white text-blue-600 mb-4 border border-gray-100">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {point.title}
                    </h3>
                    <p className="text-gray-600">{point.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Querés ver cómo encaja en tu institución?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contanos cómo trabajan hoy con imágenes y sistemas clínicos, y te
            mostramos el flujo con DICOMFiles y DICOMCapture.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center border border-white text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
          >
            Solicitar demo
            <ArrowRightIcon className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};
